import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { trackWalletEvent } from '../lib/telemetry';
import { PassData } from '../components/wallet/types';
import SiteHeader from '../components/wallet/SiteHeader';
import WalletCtaPanel from '../components/wallet/WalletCtaPanel';
import PassCard from '../components/wallet/PassCard';
import InfoTiles from '../components/wallet/InfoTiles';
import Collapsible from '../components/wallet/Collapsible';
import PassDetailsSection from '../components/wallet/PassDetailsSection';
import BenefitsList from '../components/wallet/BenefitsList';
import HowItWorks from '../components/wallet/HowItWorks';
import HelpCard from '../components/wallet/HelpCard';
import PageFooter from '../components/wallet/PageFooter';
import ErrorState from '../components/wallet/ErrorState';
import DownloadChallenge from '../components/wallet/DownloadChallenge';
import MultiPassList, { GroupPassData } from '../components/wallet/MultiPassList';
import { CtaSkeleton, PassCardSkeleton, TilesSkeleton } from '../components/wallet/Skeletons';

// Public add2wallet.link pass landing page (2026-08 redesign).
//
// This file is the orchestrator only — layout pieces live in
// src/components/wallet/. Loading is skeleton-first: the page shell renders
// immediately and the CTA stack / pass card shimmer until the keyword
// resolves, then reveal in place (no detached full-screen loader). A failed
// resolve shows a real error state instead of the old fake-data fallback.

const SUPPORT_EMAIL = 'support@mobile.digital';

const isDevHost = () =>
  window.location.hostname === 'localhost' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.startsWith('10.') ||
  window.location.hostname === '127.0.0.1';

const WalletDisplay: React.FC = () => {
  const { param1, param2, keyword } = useParams<{ param1?: string; param2?: string; keyword?: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [slowLoad, setSlowLoad] = useState(false);
  const [error, setError] = useState<{ title: string; message: string; canRetry: boolean } | null>(null);
  const [passData, setPassData] = useState<PassData | null>(null);
  // Pass download security (2026-08-30) — set when fetchPassData() gets
  // {challenge_required: true} instead of the normal preview.
  const [challengeRequired, setChallengeRequired] = useState(false);
  // Multi-pass group link (docs/MULTIPASS_SEND_PLAN.md): the same fetch can
  // return { group: true, passes: [...] } — one SMS link, N vouchers.
  const [groupPasses, setGroupPasses] = useState<GroupPassData[] | null>(null);
  const [googleAllJwt, setGoogleAllJwt] = useState<string | undefined>(undefined);
  const [apiBase, setApiBase] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  // Pass-added confirmation state. `hasClickedAdd` flips on button tap;
  // `passAdded` flips when the user returns to this tab (visibilitychange).
  // Best-effort: neither platform tells us whether the user tapped "Add" or
  // "Cancel" in the install sheet, so we treat "came back to the page after
  // clicking" as success — the standard wallet-onboarding heuristic.
  const [hasClickedAdd, setHasClickedAdd] = useState(false);
  const [passAdded, setPassAdded] = useState(false);
  // Web push opt-in state. iOS Safari technically reports support but only
  // delivers when the site is installed as a PWA — the error path surfaces a
  // friendly "add to Home Screen" hint.
  const [webPushSupported] = useState<boolean>(
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
  );
  const [webPushSubscribed, setWebPushSubscribed] = useState(false);
  const [webPushBusy, setWebPushBusy] = useState(false);
  const [webPushError, setWebPushError] = useState<string | null>(null);

  const cardRef = useRef<HTMLDivElement>(null);
  const flipTracked = useRef(false);
  const previewTracked = useRef(false);

  // Support multiple route patterns:
  // /:keyword — /wallet/:param2 — /:param1/:param2 (security_key/keyword)
  const actualParam1 = param1 || 'wallet';
  const actualParam2 = param2 || keyword;

  // Platform detect FIRST and synchronously (memo, not effect) so the CTA
  // stack renders the right buttons on the very first paint — no flash of
  // the wrong platform's layout on mobile.
  const { isIOS, isAndroid, iosMajor } = useMemo(() => {
    const userAgent = navigator.userAgent;
    const deviceParam = searchParams.get('device');
    const ios =
      deviceParam === 'ios' ||
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
    // "CPU iPhone OS 27_0" / "CPU OS 26_1" → 27 / 26. Decides whether the
    // card opens on the iOS 27 Poster face or the classic face.
    const m = userAgent.match(/(?:iPhone OS|CPU OS)\s(\d+)_/i);
    return {
      // iPadOS Safari defaults to a desktop "Macintosh" UA — the touch-point
      // check is the only signal left that it's actually an iPad.
      isIOS: ios,
      isAndroid: deviceParam === 'android' || /Android/i.test(userAgent),
      iosMajor: m ? parseInt(m[1], 10) : null,
    };
  }, [searchParams]);
  const platform: 'ios' | 'android' | 'desktop' = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
  const [previewFace, setPreviewFace] = useState<'poster' | 'classic' | 'google'>(isAndroid ? 'google' : 'classic');

  // Graceful slow-load note: only fades in when resolution takes >2s.
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setSlowLoad(true), 2000);
    return () => clearTimeout(t);
  }, [loading]);

  // WALLET_LINK_VIEWED — fire once when the landing page resolves a keyword and
  // the pass data has loaded. Emitted server-side (Option B): the API resolves
  // merchant_id from the keyword row.
  const linkViewedFired = useRef(false);
  useEffect(() => {
    if (linkViewedFired.current) return;
    if (!actualParam2 || !apiBase || !passData) return;
    linkViewedFired.current = true;
    trackWalletEvent(apiBase, actualParam2, 'WALLET_LINK_VIEWED');
  }, [actualParam2, apiBase, passData]);

  // Load API endpoint
  useEffect(() => {
    fetch('/amplify_outputs.json')
      .then(res => res.json())
      .then(config => {
        const walletEndpoint = config?.custom?.API?.WalletHttpApi?.endpoint;
        if (walletEndpoint) {
          setApiBase(walletEndpoint.replace(/\/$/, ''));
        } else {
          setApiBase('https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
        }
      })
      .catch(() => {
        setApiBase('https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
      });
  }, []);

  // Fetch pass data — the "real work" behind the skeleton: resolves the
  // keyword row (template merge + platform JWT) server-side.
  useEffect(() => {
    if (actualParam2 && apiBase) {
      fetchPassData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualParam1, actualParam2, apiBase]);

  const buildApiUrl = (suffix: string): string =>
    isDevHost() ? `/api${suffix}` : `${apiBase}${suffix}`;

  const fetchPassData = async () => {
    try {
      const tlsFingerprint = searchParams.get('tls') || '';
      const ja3Fingerprint = searchParams.get('ja3') || '';
      const apiUrl = buildApiUrl(`/${actualParam1}/${actualParam2}`);

      const response = await fetch(apiUrl, {
        headers: {
          'X-TLS-Fingerprint': tlsFingerprint,
          'X-JA3-Fingerprint': ja3Fingerprint,
          'Accept': 'application/json',
          // Always 'android': walletHandler only pre-generates the Google
          // JWT for this value, and the page now shows the Google button on
          // every platform (Apple-first on iOS). Apple's flow is unaffected —
          // it goes through /apple/{keyword} separately.
          'X-Request-Platform': 'android',
        } as any,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.challenge_required) {
          setChallengeRequired(true);
        } else if (data.group && Array.isArray(data.passes) && data.passes.length > 0) {
          setGroupPasses(data.passes);
          setGoogleAllJwt(data.googleAllJwt);
          // Seed passData with the first voucher so keyword-scoped page
          // furniture (WALLET_LINK_VIEWED effect, hero copy) still fires.
          setPassData(data.passes[0]);
        } else {
          setPassData(data);
        }
      } else if (response.status === 404 || response.status === 410) {
        setError({
          title: 'This pass link is no longer available',
          message: 'The link may have expired or been replaced. Check the most recent message from the sender, or contact support if you think this is a mistake.',
          canRetry: false,
        });
      } else if (response.status === 403) {
        setError({
          title: "This link doesn't look right",
          message: 'The pass link appears to be incomplete or invalid. Try opening the original link from your message again.',
          canRetry: false,
        });
      } else {
        setError({
          title: 'Something went wrong',
          message: "We couldn't load your pass just now. Please try again in a moment.",
          canRetry: true,
        });
      }
    } catch (err: any) {
      setError({
        title: 'Connection problem',
        message: "We couldn't reach the pass service. Check your connection and try again.",
        canRetry: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    setError(null);
    setLoading(true);
    setSlowLoad(false);
    if (actualParam2 && apiBase) fetchPassData();
  };

  // Flip `passAdded` true the first time the user returns to this tab after
  // tapping an add button — that visibilitychange-back is our proxy for
  // "the install sheet was dismissed".
  useEffect(() => {
    if (!hasClickedAdd) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') setPassAdded(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [hasClickedAdd]);

  // Web push opt-in flow — see docs/FOLLOWUP_AND_WEBPUSH.md.
  // Built from an explicit ArrayBuffer (not the length ctor) so TS ≥5.7
  // infers Uint8Array<ArrayBuffer> — the DOM's applicationServerKey type
  // rejects the ArrayBufferLike default (could be a SharedArrayBuffer).
  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  };

  const enableWebPush = async () => {
    if (!webPushSupported || webPushBusy) return;
    setWebPushBusy(true);
    setWebPushError(null);
    try {
      const keyRes = await fetch(buildApiUrl('/web-push/vapid-public-key'));
      if (!keyRes.ok) {
        // Common failure modes:
        //   403 Missing Authentication Token → /web-push/* routes not deployed
        //   500 "VAPID secret has no SecretString" → secret not seeded
        const text = await keyRes.text().catch(() => '');
        throw new Error(`VAPID key fetch failed: ${keyRes.status} ${keyRes.statusText} — ${text.slice(0, 200)}`);
      }
      const { key: vapidPublicKey } = await keyRes.json();
      if (!vapidPublicKey) throw new Error('VAPID key not configured on server');

      const reg = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Reuse an existing subscription if one is already bound to this SW —
      // avoids the user being prompted twice.
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
      }

      const securityKey = (actualParam1 && actualParam1 !== 'wallet') ? actualParam1 : undefined;
      const subRes = await fetch(buildApiUrl('/web-push/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: actualParam2,
          securityKey,
          subscription: subscription.toJSON(),
        }),
      });
      if (!subRes.ok) {
        const text = await subRes.text();
        throw new Error(`Backend rejected subscription: ${subRes.status} ${text}`);
      }
      setWebPushSubscribed(true);
    } catch (err: any) {
      console.error('Web push opt-in failed:', err);
      const msg = err?.name === 'NotAllowedError'
        ? "Notifications were blocked. On iOS, you'll need to add this page to your Home Screen first."
        : (err?.message || 'Could not enable notifications');
      setWebPushError(msg);
    } finally {
      setWebPushBusy(false);
    }
  };

  // Fire-and-forget: ask the backend to schedule a 5-minute follow-up push
  // the moment the user taps an add button. keepalive so the request survives
  // the imminent navigation (iOS .pkpass download hands over to Wallet).
  const scheduleFollowupPush = () => {
    if (!actualParam2) return;
    const securityKeyParam = (actualParam1 && actualParam1 !== 'wallet')
      ? `?k=${encodeURIComponent(actualParam1)}`
      : '';
    fetch(buildApiUrl(`/pass-followup/${actualParam2}${securityKeyParam}`), {
      method: 'POST',
      keepalive: true,
    }).catch((err) => {
      console.warn('Follow-up push schedule failed (non-fatal):', err);
    });
  };

  const handleAppleWallet = async () => {
    if (isDownloading) return; // Prevent double-clicks

    setIsDownloading(true);
    setHasClickedAdd(true);
    trackWalletEvent(apiBase, actualParam2, 'WALLET_APPLE_BUTTON_CLICKED');
    scheduleFollowupPush();

    // Pass the security_key (param1 from the landing URL) as a query so the
    // /apple/{param2} Lambda can validate it. Otherwise security_key_required
    // keywords return 403 "Access denied" on the download fetch.
    const securityKeyParam = (actualParam1 && actualParam1 !== 'wallet')
      ? `?k=${encodeURIComponent(actualParam1)}`
      : '';
    const apiUrl = buildApiUrl(`/apple/${actualParam2}${securityKeyParam}`);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Request-Download-Url': 'true',  // Signal we want the URL, not redirect
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.downloadUrl) {
          // Navigate to the S3 presigned URL to download the pass
          window.location.href = data.downloadUrl;
        } else {
          console.error('No download URL in response:', data);
        }
      } else {
        // Fallback: try direct navigation (302 redirect)
        window.location.href = apiUrl;
      }
    } catch (err) {
      console.error('Error downloading Apple pass:', err);
      window.location.href = apiUrl;
    }

    // Reset after a delay (in case navigation fails)
    setTimeout(() => setIsDownloading(false), 8000);
  };

  const handleGoogleWallet = () => {
    if (!passData?.googleJwt) return;
    setHasClickedAdd(true);
    trackWalletEvent(apiBase, actualParam2, 'WALLET_GOOGLE_BUTTON_CLICKED');
    scheduleFollowupPush();
    setShowGoogleModal(true);
  };

  // Samsung: the cdata token expires ~30s after minting, so the link is
  // minted at TAP time — direct navigation to walletHandler with
  // ?platform=samsung, which 302s to the fresh a.swallet.link URL.
  const handleSamsungWallet = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setHasClickedAdd(true);
    trackWalletEvent(apiBase, actualParam2, 'WALLET_SAMSUNG_BUTTON_CLICKED');
    scheduleFollowupPush();
    // Mirror the apple-handler path rules: two-param links carry the
    // security key in the path; legacy single-param links use /wallet/{kw}.
    const samsungPath = (actualParam1 && actualParam1 !== 'wallet')
      ? `/${actualParam1}/${actualParam2}`
      : `/wallet/${actualParam2}`;
    window.location.href = buildApiUrl(`${samsungPath}?platform=samsung`);
    setTimeout(() => setIsDownloading(false), 8000);
  };

  const handlePreview = () => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (!previewTracked.current) {
      previewTracked.current = true;
      trackWalletEvent(apiBase, actualParam2, 'WALLET_PASS_PREVIEWED');
    }
  };

  const handleFlip = () => {
    setIsFlipped((v) => !v);
    if (!flipTracked.current) {
      flipTracked.current = true;
      trackWalletEvent(apiBase, actualParam2, 'WALLET_PASS_FLIPPED');
    }
  };

  const trackSectionExpand = () => trackWalletEvent(apiBase, actualParam2, 'WALLET_SECTION_EXPANDED');
  const trackHelp = () => trackWalletEvent(apiBase, actualParam2, 'WALLET_HELP_CLICKED');

  const contactSupport = () => {
    trackHelp();
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Help adding wallet pass${actualParam2 ? ` (${actualParam2})` : ''}`)}`;
  };

  // Sections start open on desktop (matches the target design's expanded
  // desktop layout) and collapsed on mobile.
  const desktopDefault = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
    []
  );

  const googleSaveUrl = passData?.googleJwt
    ? `https://pay.google.com/gp/v/save/${passData.googleJwt}`
    : '';

  const reveal = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35 },
  };

  // Shared CTA props — the panel renders in three slots (desktop full stack,
  // mobile badges above the card, mobile preview below it).
  const ctaProps = {
    isIOS,
    isAndroid,
    googleReady: !!passData?.googleJwt,
    isDownloading,
    onApple: handleAppleWallet,
    onGoogle: handleGoogleWallet,
    onSamsung: handleSamsungWallet,
    samsungAvailable: !!passData?.samsung_available,
    onPreview: handlePreview,
    passAdded,
    onResetAdded: () => { setPassAdded(false); setHasClickedAdd(false); },
    webPush: {
      supported: webPushSupported,
      subscribed: webPushSubscribed,
      busy: webPushBusy,
      error: webPushError,
      onEnable: enableWebPush,
    },
  };

  // Device-aware wallet name for the hero. Samsung phones are Android and
  // add passes via Google Wallet today (Samsung Wallet support is the
  // "coming soon" item under Add-to-another-wallet), so they get the Google
  // wording rather than a wallet we can't deliver to yet.
  const walletName = isIOS ? 'Apple ' : isAndroid ? 'Google ' : 'Wallet';

  // Shared hero heading — rendered above the card on mobile and in the left
  // column on desktop (each hidden at the other breakpoint).
  const heroHeading = (
    <>
      Add 2 {' '}
      <span className="relative inline-block text-[#0A3B2C] whitespace-nowrap">
        <span className="relative z-10">{walletName}</span>
        {/* lime highlight bar — same treatment as the home page .hl */}
        <span
          aria-hidden
          className="absolute left-0 right-0 rounded-[3px]"
          style={{ bottom: '0.06em', height: '0.32em', background: '#AEDD1F', opacity: 0.55 }}
        />
      </span> 
    </>
  );

  return (
    <div
      className="min-h-screen text-[#06251B]"
      style={{
        // Same feel as the home page hero: cream fading to white, Inter stack.
        background: 'linear-gradient(180deg, #F5F8F1, #ffffff 60%)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <SiteHeader onContactSupport={contactSupport} />

      {error ? (
        <ErrorState
          title={error.title}
          message={error.message}
          canRetry={error.canRetry}
          onRetry={retry}
        />
      ) : challengeRequired ? (
        <DownloadChallenge
          buildApiUrl={buildApiUrl}
          keyword={actualParam2 || ''}
          onVerified={() => {
            setChallengeRequired(false);
            setLoading(true);
            fetchPassData();
          }}
        />
      ) : (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
          {groupPasses ? (
            <>
              {/* Multi-pass group link: one SMS link, N vouchers. Each pass
                  renders as a full card with its own add buttons. */}
              <div className="pt-6 lg:pt-10 text-center max-w-xl mx-auto">
                <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight mb-3">
                  {heroHeading}
                </h1>
                <p className="text-base text-gray-600">
                  You have {groupPasses.length} passes — add each one to your wallet.
                </p>
              </div>

              <div className="mt-8">
                <MultiPassList
                  passes={groupPasses}
                  isIOS={isIOS}
                  isAndroid={isAndroid}
                  groupKeyword={actualParam2}
                  groupSecurityKey={(actualParam1 && actualParam1 !== 'wallet') ? actualParam1 : undefined}
                  googleAllJwt={googleAllJwt}
                  buildApiUrl={buildApiUrl}
                  onTrack={(kw, ev) => trackWalletEvent(apiBase, kw, ev)}
                  onFollowup={(kw, sk) => {
                    fetch(buildApiUrl(`/pass-followup/${kw}${sk ? `?k=${encodeURIComponent(sk)}` : ''}`), {
                      method: 'POST',
                      keepalive: true,
                    }).catch((err) => console.warn('Follow-up push schedule failed (non-fatal):', err));
                  }}
                  onAnyAddClicked={() => setHasClickedAdd(true)}
                />
              </div>

              <div className="mt-6 max-w-md mx-auto">
                <HelpCard keyword={actualParam2} onContact={trackHelp} />
              </div>

              <PageFooter />
            </>
          ) : (
            <>
          {/* Mobile order: heading → pass card (flip on top) → CTA stack.
              Desktop: two columns, heading + CTAs left, card right — done
              with `order-*` swaps so the markup stays in one place. */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 lg:gap-14 pt-4 lg:pt-12 items-start">
            {/* Desktop-only left column: heading, supporting line and the
                full CTA stack. On mobile everything renders in the card
                section instead (badges above the card, Preview below). */}
            <section className="hidden lg:block lg:order-1 text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight mb-3">
                {heroHeading}
              </h1>
              <p className="text-base text-gray-600 mb-6 lg:mb-8">
                Get easy access to your rewards, offers and updates — right in your mobile wallet.
              </p>

              {loading ? (
                <CtaSkeleton />
              ) : (
                <motion.div {...reveal}>
                  <WalletCtaPanel variant="full" {...ctaProps} />
                </motion.div>
              )}
            </section>

            <section ref={cardRef} className="order-1 lg:order-2 max-w-md mx-auto w-full lg:max-w-none lg:mx-0">
              {/* No hero heading on mobile — the header's typewriter carries
                  the nudge so buttons + the full pass fit on one screen. */}
              {/* Mobile: wallet buttons on top, then the card, then Preview. */}
              <div className="lg:hidden mb-4">
                {loading ? (
                  <CtaSkeleton />
                ) : (
                  <motion.div {...reveal}>
                    <WalletCtaPanel variant="main" {...ctaProps} />
                  </motion.div>
                )}
              </div>

              {loading ? (
                <PassCardSkeleton slow={slowLoad} />
              ) : passData ? (
                <motion.div {...reveal}>
                  <PassCard
                    passData={passData}
                    isFlipped={isFlipped}
                    onFlip={handleFlip}
                    platform={platform}
                    iosMajor={iosMajor}
                    onFaceChange={setPreviewFace}
                  />


                  {/* Featured Actions (Apple iOS 27 top-level featuredActions /
                      Google's front CTA) — max 2, rendered as their own cards
                      BELOW the pass per Apple's documented layout, not drawn
                      inside the pass face. These also still appear in the
                      full link list below since that's what's actually on
                      the real pass's back/details view too. */}
                  {previewFace === 'poster' && passData.apple_poster && (
                    <>
                      {/* Wallet's page dots sit between the pass and the tiles. */}
                      <div className="mt-3 flex justify-center gap-1.5" aria-hidden>
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                      </div>
                      {passData.apple_poster.featuredActions.length > 0 && (
                        <FeaturedActionTiles
                          actions={passData.apple_poster.featuredActions.slice(0, 2).map((f) => ({
                            id: f.identifier, label: f.label, url: f.url, actionType: f.type,
                          }))}
                        />
                      )}
                    </>
                  )}

                  {/* Merchant action links (walletActions, e.g. "Shop now") —
                      the same links appear on the pass in the wallet apps. */}
                  {previewFace === 'google' && passData.walletActions && passData.walletActions.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {passData.walletActions.slice(0, 10).map((a) => (
                        <a
                          key={a.id}
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-center py-3 px-4 rounded-xl bg-white border border-[#E4EAE2] text-sm font-semibold text-gray-800 hover:bg-gray-50 transition break-words"
                        >
                          {a.label}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Mobile CTA panel sits AFTER the dots + featured tiles so
                      the pass, dots and tiles read as one block, the way
                      Wallet stacks them on the device (2026-10-13). */}
                  <div className="lg:hidden mt-4">
                    <WalletCtaPanel variant="preview" {...ctaProps} />
                  </div>
                </motion.div>
              ) : null}
            </section>
          </div>

          {/* Info tiles — real pass metadata only. */}
          <div className="mt-10">
            {loading ? <TilesSkeleton /> : passData ? (
              <motion.div {...reveal}><InfoTiles passData={passData} /></motion.div>
            ) : null}
          </div>

          {/* Collapsible sections. */}
          {!loading && passData && (
            <>
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <Collapsible title="Pass details" defaultOpen={desktopDefault} onFirstExpand={trackSectionExpand}>
                  <PassDetailsSection passData={passData} />
                </Collapsible>
                <Collapsible title="What you'll get" defaultOpen={desktopDefault} onFirstExpand={trackSectionExpand}>
                  <BenefitsList passData={passData} />
                </Collapsible>
              </div>

              <div className="mt-4">
                <Collapsible title="How it works" defaultOpen={desktopDefault} onFirstExpand={trackSectionExpand}>
                  <HowItWorks passData={passData} />
                </Collapsible>
              </div>

              <div className="mt-6">
                <HelpCard keyword={actualParam2} onContact={trackHelp} />
              </div>
            </>
          )}

          <PageFooter />
            </>
          )}
        </main>
      )}

      {/* Google Wallet confirmation modal — direct pay.google.com save link
          (no GAPI widget; the direct URL sidesteps origin whitelisting). */}
      {showGoogleModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setShowGoogleModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowGoogleModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Add to Google Wallet
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Tap the button below to save this pass to your Google Wallet.
            </p>

            <div className="flex justify-center">
              <a
                href={googleSaveUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowGoogleModal(false)}
                className="h-[52px] inline-flex items-center justify-center hover:opacity-80 transition"
              >
                <img
                  src="/images/googleWalletLogo.svg"
                  alt="Add to Google Wallet"
                  className="h-11 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Apple iOS 27 Featured Action tiles, drawn as Apple Wallet draws them under
// the pass: white tile, coloured icon badge, Apple's wording for the type
// (never the merchant's free-text label), "Open Link" subtitle, chevron on a
// single full-width tile. Same map as the intouch pass designer preview.
const FEATURED_STYLE: Record<string, { wording: string; color: string; sub: string }> = {
  viewOffersRewards: { wording: 'View Offers and Rewards', color: '#bf5af2', sub: 'Open Link' },
  shop: { wording: 'Shop Online or In-App', color: '#ff375f', sub: 'Open Link' },
  order: { wording: 'Order Delivery or Pickup', color: '#ff9f0a', sub: 'Open Link' },
  membershipBenefits: { wording: 'View Membership Benefits', color: '#5e5ce6', sub: 'Open Link' },
  addToBalance: { wording: 'Add to Balance', color: '#34c759', sub: 'Open Link' },
  bookAppointment: { wording: 'Book an Appointment', color: '#ff453a', sub: 'Open Link' },
  bookCar: { wording: 'Book a Car', color: '#0a84ff', sub: 'Open Link' },
  bookFlight: { wording: 'Book a Flight', color: '#0a84ff', sub: 'Open Link' },
  bookStay: { wording: 'Book a Stay', color: '#ac8e68', sub: 'Open Link' },
  viewSchedule: { wording: 'View Schedule', color: '#ff453a', sub: 'Open Link' },
  watchTrailer: { wording: 'Watch Trailer', color: '#ff9f0a', sub: 'Open Link' },
  listenToMusic: { wording: 'Listen to Music', color: '#ff2d55', sub: 'Open Link' },
  call: { wording: 'Call', color: '#34c759', sub: 'Call' },
  place: { wording: 'Go to Location', color: '#0a84ff', sub: 'Open in Maps' },
};

// Glyph inside the coloured badge, as Wallet draws it (star for offers,
// calendar for appointments/schedules); other types keep a plain dot.
const FeaturedIcon: React.FC<{ type?: string }> = ({ type }) => {
  if (type === 'viewOffersRewards' || type === 'membershipBenefits') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" aria-hidden>
        <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" />
      </svg>
    );
  }
  if (type === 'bookAppointment' || type === 'viewSchedule') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
        <rect x="4" y="5.5" width="16" height="15" rx="2.5" /><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
      </svg>
    );
  }
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" aria-hidden><circle cx="12" cy="12" r="5" /></svg>;
};

const FeaturedActionTiles: React.FC<{ actions: Array<{ id: string; label: string; url: string; actionType?: string }> }> = ({ actions }) => {
  const single = actions.length === 1;
  return (
    <div className={`mt-3 grid gap-2 ${single ? 'grid-cols-1' : 'grid-cols-2'}`}>
      {actions.map((a) => {
        const st = FEATURED_STYLE[a.actionType || ''] || { wording: a.label, color: '#8e8e93', sub: 'Open Link' };
        return (
          <a
            key={`featured-${a.id}`}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-2xl p-3 shadow-sm border border-black/5 flex items-center gap-3 hover:shadow transition"
          >
            <div className="flex-1 min-w-0">
              <span className="inline-flex w-7 h-7 rounded-full items-center justify-center mb-2" style={{ backgroundColor: st.color }} aria-hidden>
                <FeaturedIcon type={a.actionType} />
              </span>
              <p className="text-sm font-bold text-[#1c1c1e] leading-tight">{st.wording}</p>
              <p className="text-xs text-[#8e8e93] mt-0.5">{st.sub}</p>
            </div>
            {single && (
              <svg className="w-4 h-4 text-[#c7c7cc] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
            )}
          </a>
        );
      })}
    </div>
  );
};

export default WalletDisplay;
